import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { pagesAPI } from '../services/api';
import { FaArrowLeft, FaSpinner, FaFileContract, FaShieldAlt, FaQuestionCircle, FaInfoCircle, FaBell, FaCog, FaUser, FaSignOutAlt, FaClock, FaCalendarAlt, FaBuilding } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { getProfilePicture, getUploadUrl } from '../utils/helpers';

// Page configuration
const pageConfig = {
  'Terms of Service': {
    icon: FaFileContract,
    description: 'Terms and conditions for using MatchUs'
  },
  'Privacy Policy': {
    icon: FaShieldAlt,
    description: 'How we protect your privacy and data'
  },
  'Support': {
    icon: FaQuestionCircle,
    description: 'Get help and find answers'
  },
  'About Us': {
    icon: FaInfoCircle,
    description: 'Learn about MatchUs and our mission'
  },
  'Safety Tips': {
    icon: FaShieldAlt,
    description: 'Stay safe while using our platform'
  }
};

const Page = () => {
  const { pageTitle } = useParams();
  const navigate = useNavigate();
  const { user, logout: authLogout } = useAuth();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const logout = async () => {
    try {
      await authLogout();
      navigate('/login');
    } catch (error) {
      console.error('Page: Logout error:', error);
    }
  };

  useEffect(() => {
    const loadPage = async () => {
      try {
        const response = await pagesAPI.getPage(pageTitle);
        const data = response.data || response;

        if (data && data.success && data.page) {
          setPage(data.page);
        } else {
          toast.error('Page not found');
          navigate('/');
        }
      } catch (error) {
        console.warn('Using offline page mode (API unavailable):', error.message);
      } finally {
        setLoading(false);
      }
    };

    if (pageTitle) {
      loadPage();
    }
  }, [pageTitle, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin h-8 w-8 text-pink-500" />
          <p className="text-gray-600 mt-2">Loading...</p>
        </div>
      </div>
    );
  }

  if (!page) {
    return null;
  }

  const config = pageConfig[page.title] || { icon: FaInfoCircle, description: '' };
  const IconComponent = config.icon;

  return (
    <div className="min-h-screen bg-white">
      {/* MatchUs Header - Same as Encounters */}
      <header className="matchus-encounters-header">
        <div className="matchus-header-container">
          <div className="matchus-header-left">
            <Link to="/encounters" className="matchus-logo">
              <img src="/assets/images/logo-matchus.svg" alt="MatchUs" className="h-6 w-6 mr-2" />
              <span className="matchus-brand-name">Match Us</span>
            </Link>
          </div>

          <div className="matchus-header-center">
            <h1 className="matchus-page-title">{page.title}</h1>
          </div>

          <div className="matchus-header-right">
            <button className="matchus-header-btn" title="Notifications">
              <FaBell />
              <span className="matchus-notification-badge">0</span>
            </button>

            <div className="matchus-user-menu">
              <button 
                className="matchus-user-btn"
                onClick={() => setShowUserDropdown(!showUserDropdown)}
              >
                <img
                  src={user?.profile_photo ? getUploadUrl(`user-${user.id}/${user.profile_photo}`) : getProfilePicture(user || { id: 1 })}
                  alt="Profile"
                  className="matchus-user-avatar"
                />
                <span className="matchus-user-name">{user?.full_name?.split(' ')[0] || 'You'}</span>
              </button>

              <div className={`matchus-user-dropdown ${showUserDropdown ? 'show' : ''}`}>
                <Link to="/settings" className="matchus-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                  <FaCog />
                  <span>Settings</span>
                </Link>
                <Link to={`/user/${user?.id}`} className="matchus-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                  <FaUser />
                  <span>Profile</span>
                </Link>
                <div className="matchus-dropdown-divider"></div>
                {(user?.is_admin === 1 || user?.role?.name === 'admin' || user?.role?.name === 'moderator') && (
                  <Link to="/admin" className="matchus-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                    <FaCog />
                    <span>Admin Panel</span>
                  </Link>
                )}
                <button
                  type="button"
                  className="matchus-dropdown-logout"
                  onClick={() => {
                    setShowUserDropdown(false);
                    logout();
                  }}
                >
                  <FaSignOutAlt />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Overlay to close dropdown */}
      {showUserDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowUserDropdown(false)}
        />
      )}

      {/* Full Screen Professional Content */}
      <div className="bg-gradient-to-br from-gray-50 via-white to-pink-50 min-h-[calc(100vh-160px)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12 pb-24">
          {/* Hero Section - Professional & Full Width */}
          <div className={`bg-gradient-to-r ${
            page.title === 'Terms of Service' ? 'from-blue-500 to-purple-600' :
            page.title === 'Privacy Policy' ? 'from-purple-500 to-indigo-600' :
            page.title === 'Support' ? 'from-green-500 to-teal-600' :
            page.title === 'About Us' ? 'from-orange-500 to-red-600' :
            'from-pink-500 to-purple-600'
          } rounded-3xl shadow-2xl overflow-hidden mb-8`}>
            <div className="relative px-8 py-16 lg:py-20">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 w-48 h-48 bg-white rounded-full -translate-x-24 -translate-y-24"></div>
                <div className="absolute top-1/2 right-0 w-40 h-40 bg-white rounded-full translate-x-20 -translate-y-20"></div>
                <div className="absolute bottom-0 left-1/3 w-36 h-36 bg-white rounded-full -translate-x-18 translate-y-18"></div>
              </div>

              <div className="relative flex flex-col lg:flex-row items-center lg:items-start space-y-10 lg:space-y-0 lg:space-x-10">
                {/* Icon */}
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 lg:w-20 lg:h-20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white border-opacity-20 hover:border-opacity-30 transition-all duration-300">
                    <IconComponent className="h-8 w-8 lg:h-10 lg:w-10 text-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 text-center lg:text-left">
                  <h1 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4 leading-tight">
                    {page.title}
                  </h1>
                  <p className="text-lg lg:text-xl text-white text-opacity-90 mb-6 max-w-2xl leading-relaxed">
                    {(() => {
                      switch (page.title) {
                        case 'Terms of Service':
                          return 'Understand your rights and responsibilities when using MatchUs platform';
                        case 'Privacy Policy':
                          return 'Learn how we protect and handle your personal information';
                        case 'Support':
                          return 'Find answers to your questions and get help from our team';
                        case 'About Us':
                          return 'Discover our mission and what makes MatchUs special';
                        case 'Safety Tips':
                          return 'Essential safety guidelines for a secure dating experience';
                        default:
                          return '';
                      }
                    })()}
                  </p>
                  <div className="flex flex-wrap justify-center lg:justify-start gap-2">
                    <div className="backdrop-blur-sm rounded px-2 py-1 border border-white border-opacity-10 hover:border-opacity-20 hover:bg-white hover:bg-opacity-5 transition-all duration-200">
                      <div className="text-white flex items-center gap-1.5">
                        <FaClock className="text-xs opacity-80" />
                        <div className="text-xs leading-tight">
                          <div className="font-medium opacity-70">Reading Time</div>
                          <div className="font-semibold">3-5 min</div>
                        </div>
                      </div>
                    </div>
                    <div className="backdrop-blur-sm rounded px-2 py-1 border border-white border-opacity-10 hover:border-opacity-20 hover:bg-white hover:bg-opacity-5 transition-all duration-200">
                      <div className="text-white flex items-center gap-1.5">
                        <FaCalendarAlt className="text-xs opacity-80" />
                        <div className="text-xs leading-tight">
                          <div className="font-medium opacity-70">Last Updated</div>
                          <div className="font-semibold">Dec 27, 2025</div>
                        </div>
                      </div>
                    </div>
                    <div className="backdrop-blur-sm rounded px-2 py-1 border border-white border-opacity-10 hover:border-opacity-20 hover:bg-white hover:bg-opacity-5 transition-all duration-200">
                      <div className="text-white flex items-center gap-1.5">
                        <FaBuilding className="text-xs opacity-80" />
                        <div className="text-xs leading-tight">
                          <div className="font-medium opacity-70">Published by</div>
                          <div className="font-semibold">MatchUs Team</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Card */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">

            {/* Content Body */}
            <div className="px-8 lg:px-12 py-10 lg:py-16">
              <div
                className="prose prose-2xl prose-gray max-w-none
                  prose-headings:text-gray-900 prose-headings:font-bold prose-headings:scroll-mt-24
                  prose-h1:text-4xl prose-h1:mb-8 prose-h1:mt-0 prose-h1:lg:text-5xl
                  prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:text-gray-800 prose-h2:border-b prose-h2:border-gray-200 prose-h2:pb-4
                  prose-h3:text-2xl prose-h3:mt-10 prose-h3:mb-4 prose-h3:text-gray-800
                  prose-h4:text-xl prose-h4:mt-8 prose-h4:mb-3 prose-h4:text-gray-700
                  prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6 prose-p:text-lg
                  prose-strong:text-gray-900 prose-strong:font-semibold
                  prose-ul:space-y-4 prose-ol:space-y-4 prose-ul:my-8 prose-ol:my-8
                  prose-li:text-gray-700 prose-li:text-lg prose-li:leading-relaxed
                  prose-a:text-pink-600 prose-a:no-underline hover:prose-a:text-pink-800 prose-a:font-medium
                  prose-blockquote:border-l-4 prose-blockquote:border-pink-500 prose-blockquote:bg-pink-50 prose-blockquote:p-6 prose-blockquote:rounded-r-lg prose-blockquote:my-8
                  prose-code:bg-gray-100 prose-code:px-3 prose-code:py-1 prose-code:rounded-md prose-code:text-sm prose-code:font-mono prose-code:text-gray-800
                  prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:p-6 prose-pre:rounded-lg prose-pre:overflow-x-auto
                  prose-table:border-collapse prose-th:bg-gray-50 prose-th:border prose-th:border-gray-300 prose-th:px-4 prose-th:py-3 prose-th:text-left prose-th:font-semibold
                  prose-td:border prose-td:border-gray-300 prose-td:px-4 prose-td:py-3"
                dangerouslySetInnerHTML={{ __html: page.content.replace(/<h1[^>]*>.*?<\/h1>/gi, '') }}
              />
            </div>

          </div>

          {/* Related Actions Section */}
          <div className="mt-8 mb-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300">
              <FaArrowLeft className="h-8 w-8 text-pink-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h3>
              <p className="text-gray-600 text-sm mb-4">Have questions about this page?</p>
              <button
                onClick={() => navigate('/page/Support')}
                className="inline-block px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors text-sm font-medium"
              >
                Contact Support
              </button>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300">
              <IconComponent className={`h-8 w-8 mx-auto mb-4 ${
                page.title === 'Terms of Service' ? 'text-blue-500' :
                page.title === 'Privacy Policy' ? 'text-purple-500' :
                page.title === 'Support' ? 'text-green-500' :
                page.title === 'About Us' ? 'text-orange-500' :
                'text-red-500'
              }`} />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Related Pages</h3>
              <p className="text-gray-600 text-sm mb-4">Check our other important pages</p>
              <div className="flex flex-wrap justify-center gap-2">
                {page.title !== 'Terms of Service' && (
                  <button
                    onClick={() => navigate('/page/Terms of Service')}
                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-xs font-medium"
                  >
                    Terms
                  </button>
                )}
                {page.title !== 'Privacy Policy' && (
                  <button
                    onClick={() => navigate('/page/Privacy Policy')}
                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 transition-colors text-xs font-medium"
                  >
                    Privacy
                  </button>
                )}
                {page.title !== 'Support' && (
                  <button
                    onClick={() => navigate('/page/Support')}
                    className="px-3 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors text-xs font-medium"
                  >
                    Support
                  </button>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300">
              <FaArrowLeft className="h-8 w-8 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Quick Actions</h3>
              <p className="text-gray-600 text-sm mb-4">Navigate back or go home</p>
              <div className="flex flex-col space-y-2">
                <button
                  onClick={() => navigate('/encounters')}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                >
                  Go to Matches
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Page;
